<template>
  <div
    ref="rootRef"
    class="desktop-swipe"
    data-no-touch-simulate
    @pointerdown="onPointerDown"
    @click.capture="onClickCapture"
  >
    <van-swipe ref="swipeRef" v-bind="$attrs">
      <slot />
    </van-swipe>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue';

defineOptions({ inheritAttrs: false });

const rootRef = ref(null);
const swipeRef = ref(null);
const pointerState = ref(null);
let suppressClick = false;
let suppressClickTimer = 0;

function isMousePointer(event) {
  return event.pointerType === 'mouse' || event.pointerType === 'pen';
}

function onPointerDown(event) {
  if (!isMousePointer(event) || event.button !== 0) {
    return;
  }

  pointerState.value = {
    id: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    horizontal: false
  };
  window.addEventListener('pointermove', onPointerMove, true);
  window.addEventListener('pointerup', onPointerUp, true);
  window.addEventListener('pointercancel', onPointerCancel, true);
}

function onPointerMove(event) {
  const state = pointerState.value;
  if (!state || state.id !== event.pointerId) {
    return;
  }

  const deltaX = event.clientX - state.startX;
  const deltaY = event.clientY - state.startY;
  if (!state.horizontal && Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
    return;
  }

  if (!state.horizontal) {
    if (Math.abs(deltaY) >= Math.abs(deltaX)) {
      releasePointer(event.pointerId);
      return;
    }
    state.horizontal = true;
  }

  event.preventDefault();
}

function onPointerUp(event) {
  const state = pointerState.value;
  if (!state || state.id !== event.pointerId) {
    return;
  }

  const deltaX = event.clientX - state.startX;
  if (state.horizontal && Math.abs(deltaX) >= 36) {
    suppressClick = true;
    if (deltaX < 0) {
      swipeRef.value?.next?.();
    } else {
      swipeRef.value?.prev?.();
    }
    window.clearTimeout(suppressClickTimer);
    suppressClickTimer = window.setTimeout(() => {
      suppressClick = false;
    }, 250);
  }

  releasePointer(event.pointerId);
}

function onPointerCancel(event) {
  if (pointerState.value?.id === event.pointerId) {
    releasePointer(event.pointerId);
  }
}

function releasePointer(pointerId) {
  window.removeEventListener('pointermove', onPointerMove, true);
  window.removeEventListener('pointerup', onPointerUp, true);
  window.removeEventListener('pointercancel', onPointerCancel, true);
  pointerState.value = null;
}

function onClickCapture(event) {
  if (!suppressClick) {
    return;
  }

  window.clearTimeout(suppressClickTimer);
  suppressClick = false;
  event.preventDefault();
  event.stopPropagation();
}

function swipeTo(index) {
  return swipeRef.value?.swipeTo?.(index);
}

onBeforeUnmount(() => {
  window.clearTimeout(suppressClickTimer);
  window.removeEventListener('pointermove', onPointerMove, true);
  window.removeEventListener('pointerup', onPointerUp, true);
  window.removeEventListener('pointercancel', onPointerCancel, true);
});

defineExpose({ swipeTo });
</script>

<style scoped>
.desktop-swipe {
  min-width: 0;
  touch-action: pan-y;
  user-select: none;
  cursor: grab;
}

.desktop-swipe:active {
  cursor: grabbing;
}
</style>
